import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { searchClients } from '@/services/clients.js';

const DEBOUNCE_MS = 300;

export default function CustomerSelect({
  label,
  value,
  selectedCustomer,
  onSelect,
  onOpenModal,
  inputName,
  autoFocus = false,
  onInputKeyDown,
  placeholder = 'Buscar cliente...',
  disabled = false,
  description,
  density = 'default',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapperRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    if (selectedCustomer?.razonSocial) {
      setQuery(selectedCustomer.razonSocial);
    } else if (!value) {
      setQuery('');
    }
  }, [selectedCustomer, value]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setLoading(false);
      setResults([]);
      return;
    }

    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      try {
        const response = await searchClients({ search: query, pageSize: 8 }, { signal: abortRef.current.signal });
        setResults(response.data || []);
        setOpen(true);
      } catch (error) {
        if (error?.message !== 'La solicitud fue cancelada.') {
          console.error('Error buscando clientes', error);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [query]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const suggestions = useMemo(() => {
    if (!query || query.trim().length < 2) return [];
    return results;
  }, [query, results]);

  const handleSelect = customer => {
    setQuery(customer?.razonSocial || '');
    setOpen(false);
    if (onSelect) {
      onSelect(customer);
    }
  };

  const isCompact = density === 'compact';
  const labelClassName = isCompact
    ? 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-[rgb(var(--muted))]'
    : 'mb-1 block text-sm font-medium text-[rgb(var(--fg))]';
  const descriptionClassName = isCompact
    ? 'mb-1.5 text-[11px] text-[rgb(var(--muted))]'
    : 'mb-2 text-xs text-[rgb(var(--muted))]';
  const inputClassName = isCompact
    ? 'w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-9 py-1.5 text-[13px] text-[rgb(var(--fg))] shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]'
    : 'w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-9 py-2.5 text-sm text-[rgb(var(--fg))] shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';
  const buttonClassName = isCompact
    ? 'inline-flex h-8 items-center gap-1.5 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-2.5 text-[12px] font-semibold text-[rgb(var(--fg))] shadow-sm transition hover:bg-[rgba(var(--muted),0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]'
    : 'inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-4 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition hover:bg-[rgba(var(--muted),0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]';

  return (
    <div ref={wrapperRef} className="relative">
      {label ? (
        <label className={labelClassName}>{label}</label>
      ) : null}
      {description ? <p className={descriptionClassName}>{description}</p> : null}
      <div className="flex items-stretch gap-1.5">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]"
            aria-hidden="true"
          />
          <input
            type="text"
            name={inputName}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onInputKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            className={inputClassName}
            aria-expanded={open}
            aria-controls={listId}
          />
          {open ? (
            <div className="absolute z-20 mt-2 w-full rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)]">
              {loading ? (
                <div className="px-4 py-3 text-sm text-[rgb(var(--muted))]">
                  Buscando clientes...
                </div>
              ) : null}
              {!loading && suggestions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-[rgb(var(--muted))]">
                  Sin resultados. Probá con otra búsqueda.
                </div>
              ) : null}
              {!loading && suggestions.length > 0 ? (
                <ul id={listId} role="listbox" className="max-h-60 overflow-y-auto py-1">
                  {suggestions.map(customer => (
                    <li key={customer.id} role="option">
                      <button
                        type="button"
                        className="flex w-full flex-col gap-1 px-4 py-2 text-left text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--muted),0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                        onClick={() => handleSelect(customer)}
                      >
                        <span className="font-semibold">{customer.razonSocial}</span>
                        <span className="text-xs text-[rgb(var(--muted))]">
                          {customer.cuit ? `CUIT ${customer.cuit}` : 'Sin CUIT'} ·{' '}
                          {customer.localidad?.nombre || 'Localidad no informada'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onOpenModal}
          disabled={disabled}
          className={buttonClassName}
        >
          <UserPlus className="h-4 w-4" aria-hidden="true" />
          Buscar/Crear
        </button>
      </div>
    </div>
  );
}
